/**
 * マニュアルAPI クライアント
 * バックエンドからマニュアルPDFのURLを取得
 */
import { ENDPOINTS, getEnvironmentConfig } from '../config/apiEndpoints';

class ManualApiClient {
  constructor() {
    this.config = getEnvironmentConfig();
    this.baseURL = this.config.baseUrl || '';
  }

  /**
   * 共通のfetchメソッド
   * @private
   * @param {string} url - リクエストURL
   * @param {string} csrfToken - CSRFトークン
   * @param {string} logContext - ログ用コンテキスト
   * @param {string} errorMessage - エラー時のメッセージ
   * @returns {Promise<Object>} レスポンスデータ
   */
  async _fetchWithAuth(url, csrfToken, logContext, errorMessage) {
    try {
      
      // ヘッダーを構築
      const headers = {
        'Content-Type': 'application/json'
      };
      
      // CSRFトークンが提供されている場合は追加
      if (csrfToken) {
        headers['X-CSRF-TOKEN'] = csrfToken;
      }
      
      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include', // Cookieを含める
        headers
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        
        // CSRFエラーの特別な処理
        if (response.status === 403 && errorData.message?.includes('CSRF')) {
          throw new Error('CSRF検証エラー: セキュリティトークンが無効です');
        }
        
        throw new Error(`HTTP ${response.status}: ${errorData.message || response.statusText}`);
      }

      const data = await response.json();
      
      return data;
      
    } catch (error) {
      console.error(`[MANUAL API CLIENT] ${logContext} - Failed:`, error);
      
      // CSRFエラーの場合は特別なメッセージ
      if (error.message.includes('CSRF')) {
        throw new Error('セキュリティ検証エラーが発生しました。ページを再読み込みして再度お試しください。');
      }
      
      throw new Error(`${errorMessage}: ${error.message}`);
    }
  }

  /**
   * マニュアルPDFのURL一覧を取得
   * @param {string} manualType - マニュアル種別 (robot, smart_lock, store_layout, reservation)
   * @param {string} csrfToken - CSRFトークン
   * @returns {Promise<Object>} マニュアルURL情報
   */
  async getManualPdfUrls(manualType, csrfToken) {
    const url = `${this.baseURL}${ENDPOINTS.MANUAL.GET_URL}?manualType=${encodeURIComponent(manualType)}`;
    
    return this._fetchWithAuth(
      url,
      csrfToken,
      `Fetching manual PDFs for type: ${manualType}`,
      'マニュアルPDFの取得に失敗しました'
    );
  }

  /**
   * 利用可能なマニュアル一覧を取得
   * @param {string} csrfToken - CSRFトークン
   * @returns {Promise<Object>} 利用可能なマニュアル一覧
   */
  async getAvailableManuals(csrfToken) {
    const url = `${this.baseURL}${ENDPOINTS.MANUAL.LIST}`;
    
    return this._fetchWithAuth(
      url,
      csrfToken,
      'Fetching available manuals',
      '利用可能なマニュアル一覧の取得に失敗しました'
    );
  }

  /**
   * 全マニュアル種別一覧を取得（管理者用）
   * @param {string} csrfToken - CSRFトークン
   * @returns {Promise<Object>} 全マニュアル種別一覧
   */
  async getAllManualTypes(csrfToken) {
    const url = `${this.baseURL}${ENDPOINTS.MANUAL.ALL}`;
    
    return this._fetchWithAuth(
      url,
      csrfToken,
      'Fetching all manual types',
      '全マニュアル種別一覧の取得に失敗しました'
    );
  }

  /**
   * エラーハンドリング用のヘルパーメソッド
   * @param {Error} error - エラーオブジェクト
   * @returns {string} ユーザー向けエラーメッセージ
   */
  getErrorMessage(error) {
    const errorMappings = {
      'CSRF': 'セキュリティ検証エラーが発生しました。ページを再読み込みして再度お試しください。',
      'セキュリティ': 'セキュリティ検証エラーが発生しました。ページを再読み込みして再度お試しください。',
      '401': '認証エラーです。再度ログインしてください。',
      '403': 'このマニュアルへのアクセス権限がありません。',
      '404': '指定されたマニュアルが見つかりません。',
      '500': 'サーバーエラーが発生しました。しばらくしてから再度お試しください。'
    };

    // ステータスコードを見つける
    for (const [keyword, message] of Object.entries(errorMappings)) {
      if (error.message.includes(keyword)) {
        return message;
      }
    }

    return error.message || 'マニュアルの取得でエラーが発生しました。';
  }
}

// シングルトンインスタンスをエクスポート
export const manualApiClient = new ManualApiClient();

export default manualApiClient;